import { CloseOutlined, PlayCircleOutlined, UploadOutlined } from '@ant-design/icons';
import {
  App as AntApp,
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  Upload,
} from 'antd';
import { useMemo, useState } from 'react';
import {
  useModelTest,
  useStartSubSourceTest,
  useSubSources,
  useUploadMedia,
} from '../../api/hooks';
import type { SubSourceKind } from '../../api/types';
import { extractErrorMessage } from '../../api/client';
import { MediaResult } from '../MediaResult';

const { Text } = Typography;

const OPS: { value: SubSourceKind; label: string }[] = [
  { value: 'chat', label: 'Chat (văn bản / vision)' },
  // Ảnh dùng chung: không đính kèm ảnh = tạo ảnh; có đính kèm ảnh = sửa ảnh.
  { value: 'image', label: 'Ảnh (không kèm ảnh = tạo, có kèm ảnh = sửa)' },
];

// Kích thước/tỷ lệ ảnh — GIỐNG HỆT Zonetoken cũ: chọn từ danh sách (không nhập tay).
// Chỉ 'auto' được gửi vào tham số (size); các tỷ lệ khác Gateway tự đưa vào prompt.
const SIZE_OPTIONS = ['auto', '1:1', '3:4', '9:16', '4:3', '16:9'];

/**
 * Kiểm thử Vilao theo NGUỒN CON: chọn nguồn con + thao tác (chat/tạo ảnh/sửa ảnh),
 * nhập prompt, tải nhiều ảnh (vision/tham chiếu). Upload thật chỉ chạy khi bấm Chạy.
 */
export default function SubSourceTestTab({ providerId }: { providerId: string }) {
  const { message } = AntApp.useApp();
  const subs = useSubSources(providerId);
  const start = useStartSubSourceTest(providerId);
  const upload = useUploadMedia(providerId);

  const [subId, setSubId] = useState<string | undefined>();
  const [op, setOp] = useState<SubSourceKind>('chat');
  const [prompt, setPrompt] = useState('');
  const [size, setSize] = useState<string | undefined>();
  const [n, setN] = useState<number | undefined>();
  const [images, setImages] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});
  const [urlInput, setUrlInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [testId, setTestId] = useState<string | undefined>();

  const test = useModelTest(testId);
  const enabledSubs = useMemo(() => (subs.data ?? []).filter((s) => s.enabled), [subs.data]);
  const showImages = op === 'chat' || op === 'image';

  const stageFile = (file: File) => {
    const localUrl = URL.createObjectURL(file);
    setPendingFiles((p) => ({ ...p, [localUrl]: file }));
    setImages((arr) => [...arr, localUrl]);
  };

  const addUrl = () => {
    const url = urlInput.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      message.warning('URL phải bắt đầu bằng http:// hoặc https://');
      return;
    }
    setImages((arr) => [...arr, url]);
    setUrlInput('');
  };

  const removeImage = (url: string) => {
    if (pendingFiles[url]) {
      URL.revokeObjectURL(url);
      setPendingFiles((p) => {
        const n2 = { ...p };
        delete n2[url];
        return n2;
      });
    }
    setImages((arr) => arr.filter((u) => u !== url));
  };

  const onRun = async () => {
    if (!subId) {
      message.warning('Chọn nguồn con để kiểm thử');
      return;
    }
    // Ảnh: có thể chỉ prompt (tạo) HOẶC kèm ảnh (sửa). Cần ít nhất 1 trong 2.
    if (op === 'chat' && !prompt.trim()) {
      message.warning('Nhập prompt');
      return;
    }
    if (op === 'image' && !prompt.trim() && !images.length) {
      message.warning('Nhập prompt (tạo ảnh) hoặc đính kèm ảnh (sửa ảnh)');
      return;
    }

    // Upload ảnh đã đính kèm (objectURL) lên kho gateway để lấy URL thật.
    let resolvedImages = images;
    if (Object.keys(pendingFiles).length) {
      setUploading(true);
      try {
        const out: string[] = [];
        for (const ref of images) {
          const file = pendingFiles[ref];
          if (file) {
            const fd = new FormData();
            fd.append('mediaType', 'image');
            fd.append('file', file);
            const res = await upload.mutateAsync(fd);
            if (!res.url) throw new Error('Upload ảnh thất bại');
            out.push(res.url);
          } else out.push(ref);
        }
        resolvedImages = out;
        Object.keys(pendingFiles).forEach((u) => URL.revokeObjectURL(u));
        setPendingFiles({});
        setImages(out);
      } catch (e) {
        setUploading(false);
        message.error(extractErrorMessage(e, 'Tải ảnh lên thất bại'));
        return;
      }
      setUploading(false);
    }

    const input: Record<string, unknown> = {};
    if (prompt.trim()) input.prompt = prompt;
    if (showImages && resolvedImages.length) input.images = resolvedImages;
    if (size) input.size = size;
    if (n) input.n = n;

    try {
      const { testId: id } = await start.mutateAsync({ subId, op, input });
      setTestId(id);
      message.success('Đã gửi yêu cầu kiểm thử');
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const result = test.data;

  return (
    <div>
      <Alert
        type="info"
        message="Kiểm thử theo nguồn con — chạy theo đúng Chế độ thực thi của nhà cung cấp (worker/gateway/hybrid) như Zonetoken. Mỗi nguồn con dùng API key + model riêng. Tải nhiều ảnh cho vision (chat) hoặc ảnh tham chiếu (tạo/sửa ảnh)."
        style={{ marginBottom: 16 }}
      />
      <Row gutter={24}>
        <Col xs={24} lg={13}>
          <Form layout="vertical">
            <Form.Item label="Nguồn con" required>
              <Select
                style={{ width: '100%' }}
                placeholder="Chọn nguồn con"
                value={subId}
                onChange={setSubId}
                loading={subs.isLoading}
                showSearch
                optionFilterProp="label"
                options={enabledSubs.map((s) => ({ value: s.id, label: s.name }))}
              />
            </Form.Item>
            <Form.Item label="Thao tác" required>
              <Select<SubSourceKind>
                style={{ width: '100%' }}
                value={op}
                onChange={(v) => {
                  setOp(v);
                  setTestId(undefined);
                }}
                options={OPS}
              />
            </Form.Item>
            <Form.Item label="Prompt" required={op === 'chat'}>
              <Input.TextArea rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} />
            </Form.Item>

            {op === 'image' && (
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Kích thước / tỷ lệ">
                    <Select
                      style={{ width: '100%' }}
                      placeholder="Chọn kích thước"
                      value={size}
                      onChange={(v) => setSize(v)}
                      allowClear
                      options={SIZE_OPTIONS.map((s) => ({ value: s, label: s === 'auto' ? 'auto (tự động)' : s }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Số lượng (n)">
                    <InputNumber style={{ width: '100%' }} min={1} max={4} value={n} onChange={(v) => setN(v ?? undefined)} />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {showImages && (
              <Form.Item
                label={op === 'chat' ? 'Ảnh (vision, tuỳ chọn)' : 'Ảnh tham chiếu (có = sửa ảnh, để trống = tạo ảnh)'}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div
                    tabIndex={0}
                    onPaste={(e) => {
                      const files = Array.from(e.clipboardData?.files ?? []);
                      if (files.length) {
                        e.preventDefault();
                        files.forEach(stageFile);
                      }
                    }}
                    style={{ outline: 'none' }}
                  >
                    <Upload.Dragger
                      accept="image/*"
                      multiple
                      showUploadList={false}
                      beforeUpload={(file) => {
                        stageFile(file);
                        return Upload.LIST_IGNORE;
                      }}
                      style={{ padding: '10px 14px' }}
                    >
                      <p style={{ margin: 0, fontSize: 14 }}>
                        <UploadOutlined /> Kéo-thả ảnh vào đây, hoặc bấm để chọn (nhiều ảnh)
                      </p>
                      <p style={{ margin: '4px 0 0', fontSize: 12, color: '#8c8c8c' }}>
                        Hoặc dán (Cmd/Ctrl+V), dùng nút native bên dưới, hoặc dán URL.
                      </p>
                    </Upload.Dragger>
                  </div>
                  <label style={{ fontSize: 12, color: '#595959', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    Hoặc dùng nút gốc:
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        Array.from(e.target.files ?? []).forEach(stageFile);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <Space.Compact>
                    <Input
                      style={{ width: 280 }}
                      placeholder="hoặc dán URL ảnh"
                      value={urlInput}
                      onChange={(e) => setUrlInput(e.target.value)}
                      onPressEnter={addUrl}
                    />
                    <Button onClick={addUrl}>Thêm URL</Button>
                  </Space.Compact>
                  {images.length > 0 && (
                    <Space wrap>
                      {images.map((u) => (
                        <div key={u} style={{ position: 'relative', display: 'inline-block' }}>
                          <img src={u} alt="" style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8 }} />
                          <Button
                            size="small"
                            shape="circle"
                            icon={<CloseOutlined />}
                            onClick={() => removeImage(u)}
                            style={{ position: 'absolute', top: -8, right: -8, boxShadow: '0 1px 4px rgba(0,0,0,.3)' }}
                          />
                        </div>
                      ))}
                    </Space>
                  )}
                </Space>
              </Form.Item>
            )}

            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              loading={uploading || start.isPending}
              onClick={onRun}
            >
              {uploading ? 'Đang tải ảnh lên…' : 'Chạy kiểm thử'}
            </Button>
          </Form>
        </Col>

        <Col xs={24} lg={11}>
          <Card title="Kết quả kiểm thử" style={{ position: 'sticky', top: 8 }}>
            {!testId && <Empty description="Chưa chạy kiểm thử" />}
            {testId && test.isLoading && <Spin />}
            {testId && result && (
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  Trạng thái:{' '}
                  {result.status === 'running' && <Tag color="processing">{result.note || 'Đang chạy…'}</Tag>}
                  {result.status === 'succeeded' && <Tag color="success">Thành công</Tag>}
                  {result.status === 'failed' && <Tag color="error">Thất bại</Tag>}
                </div>
                {result.status === 'running' && <Spin />}
                {result.status === 'failed' && result.error && (
                  <Alert type="error" message={`Lỗi (${result.error.class ?? 'fatal'})`} description={result.error.message} />
                )}
                {result.status === 'succeeded' && <MediaResult output={result.output} />}
                {result.status === 'succeeded' && (
                  <Collapse
                    ghost
                    items={[
                      {
                        key: 'raw-output',
                        label: 'Output (JSON thô)',
                        children: (
                          <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 6, maxHeight: 320, overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: 12 }}>
                            {typeof result.output === 'string' ? result.output : JSON.stringify(result.output, null, 2)}
                          </pre>
                        ),
                      },
                    ]}
                  />
                )}
              </Space>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
